'use client';

import { createTheme, TextInput, Select, Button, Chip, Alert, Modal, ActionIcon, Table, Badge, Slider, RangeSlider, Paper, Title } from '@mantine/core';
import { grayColors, orangeColors, blueColors, purpleColors, yellowColors } from '../utils/colors.mjs';
var gray = Object.values(grayColors);
var orange = Object.values(orangeColors);
var blue = Object.values(blueColors);
var purple = Object.values(purpleColors);
var yellow = Object.values(yellowColors);
var commonTheme = createTheme({
  fontFamily: "var(--wallet-font-family-standard)",
  colors: {
    "brand-primary": ["var(--wallet-color-primary-shade-100)", "var(--wallet-color-primary-shade-200)", "var(--wallet-color-primary-shade-300)", "var(--wallet-color-primary-shade-400)", "var(--wallet-color-primary-shade-500)", "var(--wallet-color-primary-shade-600)", "var(--wallet-color-primary-shade-700)", "var(--wallet-color-primary-shade-800)", "var(--wallet-color-primary-shade-900)", "var(--wallet-color-primary-shade-1000)"],
    gray: gray,
    orange: orange,
    blue: blue,
    purple: purple,
    yellow: yellow
  },
  primaryColor: "brand-primary",
  components: {
    TextInput: TextInput.extend({
      defaultProps: {
        radius: "var(--wallet-border-radius-field-single-line)"
      },
      vars: function vars(theme, props) {
        if (props.ta === "center") return {
          input: {
            textAlign: "center"
          }
        };
        if (props.variant === "filled") return {
          input: {
            "--input-padding-inline-start": "var(--wallet-space-padding-field-single-line)",
            "--input-padding-inline-end": "var(--wallet-space-padding-field-single-line)"
          }
        };
        return {
          input: {
            "--input-padding-inline-start": "var(--wallet-space-padding-field-single-line)",
            "--input-padding-inline-end": "var(--wallet-space-padding-field-single-line)",
            "--input-placeholder-color": "var(--wallet-color-neutrals-gray-500)"
          },
          wrapper: {
            "--input-bd": "var(--wallet-color-neutrals-gray-200)"
          }
        };
      }
    }),
    Select: Select.extend({
      vars: function vars(theme, props) {
        if (props.variant === "invertedOutline") return {
          root: {
            "--input-bg": "transparent",
            "--input-bd": "var(--wallet-color-neutrals-white)",
            "--input-bd-focus": "var(--wallet-color-neutrals-white)",
            "--input-color": "var(--wallet-color-neutrals-white)"
          },
          input: {
            color: "var(--wallet-color-neutrals-white)"
          },
          section: {
            color: "var(--wallet-color-neutrals-white)"
          }
        };
        return {};
      }
    }),
    Button: Button.extend({
      defaultProps: {
        radius: "var(--wallet-border-radius-button)",
        variant: "filled",
        size: "md"
      },
      vars: function vars(theme, props) {
        if (props.variant === "filled") return {
          root: {
            "--button-padding-x": "var(--wallet-space-padding-button-standard)",
            "--button-bg": "var(--wallet-color-component-button-bg)",
            "--button-hover": "var(--wallet-color-component-button-hover-bg)"
          },
          label: {
            color: "var(--wallet-color-component-button-label)",
            fontWeight: "400"
          }
        };
        if (props.variant === "invertedOutline") return {
          root: {
            "--button-bg": "transparent",
            "--button-bd": "calc(0.0625rem * var(--mantine-scale)) solid var(--wallet-color-neutrals-white)",
            "--button-color": "var(--wallet-color-neutrals-white)",
            "--button-radius": "var(--wallet-border-radius-button)",
            "--button-hover": "rgba(0, 0, 0, 0.15)"
          },
          label: {
            fontWeight: "400"
          }
        };
        if (props.variant === "subtle") return {
          root: {
            "--button-color": "var(--wallet-color-component-button-subtle-label)"
          },
          label: {
            fontWeight: "400"
          }
        };
        if (props.size === "sm") return {
          root: {
            "--button-padding-x": "12px"
          },
          label: {
            fontWeight: "400"
          }
        };
        return {
          root: {
            "--button-padding-x": "var(--wallet-space-padding-button-standard)",
            fontWeight: "400"
          },
          label: {
            fontWeight: "400"
          }
        };
      }
    }),
    Chip: Chip.extend({
      vars: function vars() {
        return {
          root: {
            "--chip-bd": "1px solid var(--wallet-color-primary-shade-800)",
            "--chip-bg": "var(--wallet-color-primary-shade-100)",
            "--chip-hover": "var(--wallet-color-primary-shade-200)",
            "--chip-color": "var(--wallet-color-primary-shade-900)",
            "--chip-fz": "var(--wallet-font-size-standard)"
          }
        };
      }
    }),
    Alert: Alert.extend({
      defaultProps: {
        radius: "md"
      },
      vars: function vars() {
        return {
          root: {
            "--alert-bg": "var(--wallet-color-primary-shade-100)"
          }
        };
      }
    }),
    Modal: Modal.extend({
      defaultProps: {
        radius: "lg"
      }
    }),
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        color: "black"
      }
    }),
    Table: Table.extend({
      defaultProps: {
        highlightOnHover: true
      }
    }),
    Badge: Badge.extend({
      // @ts-expect-error The css variables are too specific but it still works so i just disabled the type check here. Maybe remove this if Mantine fixes this bug.
      vars: function vars(theme, props) {
        var backgroundColor = getNeutralColor(props.color, "200");
        var labelColor = getNeutralColor(props.color, "600");
        if (props.variant === "status") {
          return {
            root: {
              "--badge-height-xs": "calc(1.5rem * var(--mantine-scale))",
              "--badge-height-sm": "calc(1.6875rem * var(--mantine-scale))",
              "--badge-height-md": "calc(1.875rem * var(--mantine-scale))",
              "--badge-height-lg": "calc(2.4375rem * var(--mantine-scale))",
              "--badge-height-xl": "calc(3rem * var(--mantine-scale))",
              "--badge-padding-x-xs": "calc(0.75rem * var(--mantine-scale))",
              "--badge-padding-x-sm": "calc(1rem * var(--mantine-scale))",
              "--badge-padding-x-md": "calc(1.25rem * var(--mantine-scale))",
              "--badge-padding-x-lg": "calc(1.5rem * var(--mantine-scale))",
              "--badge-padding-x-xl": "calc(2rem * var(--mantine-scale))",
              "--badge-color": labelColor,
              "--badge-bg": backgroundColor,
              fontWeight: "500",
              textTransform: "unset"
            }
          };
        }
        return {};
      }
    }),
    Slider: Slider.extend({
      defaultProps: {
        color: "var(--wallet-color-primary-shade-1000)"
      },
      vars: function vars() {
        return {
          root: {
            "--slider-thumb-size": "24px"
          },
          thumb: {
            border: "calc(0.4rem * var(--mantine-scale)) solid"
          }
        };
      }
    }),
    RangeSlider: RangeSlider.extend({
      defaultProps: {
        color: "var(--wallet-color-primary-shade-1000)"
      },
      vars: function vars() {
        return {
          root: {
            "--slider-thumb-size": "24px"
          },
          thumb: {
            border: "calc(0.4rem * var(--mantine-scale)) solid"
          }
        };
      }
    }),
    Paper: Paper.extend({
      defaultProps: {
        radius: "md"
      }
    }),
    Title: Title.extend({
      // @ts-expect-error --title-ff does not exist in the Mantine types definition but it still works and exists as a css variable so i just disabled the type check here.
      vars: function vars() {
        return {
          root: {
            "--title-ff": "var(--wallet-font-family-standard)"
          }
        };
      }
    })
  }
});
var getNeutralColor = function getNeutralColor(color, shade) {
  if (color === undefined) return "var(--wallet-color-primary-shade-".concat(shade, ")");
  return "var(--wallet-color-neutrals-".concat(color, "-").concat(shade, ")");
};
export { commonTheme };